/**
 * EN — falls back to RU for any missing key (docs/06 §8). Typed as a deep-partial of the RU
 * tree, so a MISSING key is legal (it falls back) but a MISSPELLED key is a compile error —
 * which is the failure mode that actually bites, since a typo'd key silently renders the raw
 * key string to the user.
 */
import type { Translations } from './ru';

// Leaves widen to `string`: a MISSPELLED key is still a compile error, but a translated
// VALUE no longer has to equal the RU literal (ru.ts is `as const`).
type DeepPartial<T> = { [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : string };

export const en: DeepPartial<Translations> = {
  common: {
    appName: 'MOST',
    cabinet: 'Producer cabinet',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    close: 'Close',
    edit: 'Edit',
    delete: 'Delete',
    retry: 'Retry',
    loading: 'Loading…',
    yes: 'Yes',
    no: 'No',
    required: 'Required',
    optional: 'optional',
    error: 'Error',
    errorGeneric: 'Something went wrong. Please try again.',
    errorNetwork: 'No connection to the server. Check your network.',
    errorLoad: 'Could not load data.',
    logout: 'Log out',
    language: 'Language',
    of: 'of',
    page: 'Page',
    unsavedTitle: 'Unsaved changes',
    unsavedBody: 'You have changes that are not saved. Leave the page?',
    unsavedLeave: 'Leave without saving',
    unsavedStay: 'Stay',
  },

  units: {
    piece: 'piece',
    pack: 'pack',
    box: 'box',
    pallet: 'pallet',
    truck: 'truck',
    piece_plural: 'pieces',
    pack_plural: 'packs',
    box_plural: 'boxes',
    pallet_plural: 'pallets',
    truck_plural: 'trucks',
    kg: 'kg',
  },

  nav: {
    home: 'Cabinet',
    profile: 'Profile',
    products: 'Products',
    orders: 'Orders',
    notifications: 'Notifications',
  },

  status: {
    draft: 'Draft',
    active: 'Active',
    archived: 'Archived',
    awaitingPricing: 'Awaiting MOST pricing',
    order: {
      new: 'New',
      in_progress: 'In progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
  },

  home: {
    title: 'Cabinet',
    greeting: 'Welcome to your producer cabinet',
    pendingTitle: 'Profile awaiting MOST approval',
    pendingBody:
      'We are reviewing your profile. Meanwhile you can complete it and prepare products — they become visible to buyers once approved.',
    rejectedTitle: 'MOST asks you to update your profile',
    rejectedBody: 'MOST comment:',
    rejectedCta: 'Update profile and re-submit',
    stats: {
      activeProducts: 'Active products',
      draftProducts: 'Drafts',
      newOrders: 'New orders',
    },
    telegram: {
      title: 'Telegram',
      linked: 'Telegram connected ✓',
      linkedSince: 'Connected {{date}}',
      notLinked: 'Telegram not connected',
      fallbackHint:
        'Without Telegram, order alerts arrive by email and here in the cabinet.',
      connect: 'Connect Telegram',
      connecting: 'Opening Telegram…',
      unlink: 'Disconnect',
      waiting: 'Waiting for confirmation in Telegram… Keep this tab open.',
      openFailed: 'Could not create the link. Please try again.',
      why: 'Instant alerts about new orders, straight to your messenger.',
    },
  },

  profile: {
    title: 'Profile',
    subtitle: 'This screen shows exactly which data buyers see, and which only MOST sees.',
    publicSection: 'Visible to buyers',
    publicHint: 'Any buyer browsing the catalog sees these fields.',
    privateSection: 'Visible only to MOST',
    privateHint: 'Buyers never see this. MOST needs it to work with you.',
    brandName: 'Brand',
    country: 'Country',
    description: 'Description',
    productionCapacity: 'Production capacity',
    rating: 'Rating',
    ratingNone: 'No rating yet',
    ratingHint: 'The rating is set by MOST and cannot be changed here.',
    legalName: 'Legal name',
    address: 'Address',
    contactPerson: 'Contact person',
    phone: 'Phone',
    markets: 'Export markets',
    marketsHint:
      'Buyers never see your export markets in any form — neither representative contacts nor even the bare country list.',
    marketAdd: 'Add market',
    marketRemove: 'Remove market',
    marketCountry: 'Country',
    marketType: 'Type',
    marketCanExport: 'Can export',
    marketAlreadyExports: 'Already exports',
    marketRep: 'Representative contact',
    marketRepHint: 'The most sensitive field. Never shown to buyers under any circumstances.',
    marketDuplicate: 'This country + type pair is already added',
    certificates: 'Certificates',
    certificatesHint: 'Visible to buyers. PDF, JPEG, PNG or WEBP, up to 10 MB. Max {{max}}.',
    certificateTitle: 'Certificate title',
    certificateAdd: 'Upload certificate',
    certificateDelete: 'Delete certificate',
    certificateDeleteConfirm: 'Delete certificate "{{title}}"?',
    submitForApproval: 'Submit for approval',
    submitHint: 'MOST will review your profile. Brand and country are required.',
    submitted: 'Profile submitted to MOST for approval.',
    lockedTitle: 'MOST only',
  },

  products: {
    title: 'Products',
    add: 'Add product',
    filterAll: 'All',
    thumb: 'Photo',
    name: 'Name',
    category: 'Category',
    status: 'Status',
    basePrice: 'Your price',
    perPiece: 'per piece',
    actions: 'Actions',
    activate: 'Activate',
    archive: 'Archive',
    archiveConfirm: 'Archive product "{{name}}"?',
    archiveBody: 'It will disappear from the buyer catalog. You can restore it later.',
    activateConfirm: 'Activate product "{{name}}"?',
    activateBody:
      'The product will be sent to MOST for pricing. Buyers see it only once MOST sets the catalog price.',
    activated: 'Sent to MOST for pricing.',
    isPricedYes: 'In catalog',
    isPricedNo: 'Awaiting MOST pricing',
    isPricedHint: 'The product appears in the buyer catalog once MOST sets the catalog price.',
    emptyTitle: 'No products yet',
    emptyBody: 'Add your first product — it appears in the catalog after MOST reviews it.',
    emptyCta: 'Add your first product',
    lockedTitle: 'Products unlock after your profile is approved',
    lockedBody:
      'Complete your profile and submit it to MOST. Once approved, you can add products.',
    lockedCta: 'Go to profile',
  },

  wizard: {
    titleNew: 'New product',
    titleEdit: 'Edit product',
    draftSaved: 'Draft saved',
    draftSaving: 'Saving draft…',
    draftFailed: 'Could not save the draft. Nothing was lost — please try again.',
    step1: 'Basics',
    step2: 'Packaging',
    step3: 'Price',
    step4: 'Media',
    step5: 'Review',
    name: 'Product name',
    nameHint: 'As on the label. Buyers see the name exactly as you type it.',
    category: 'Category',
    categoryPlaceholder: 'Select a category',
    categoryLoadFailed: 'Could not load categories.',
    description: 'Description',
    descriptionHint: 'For example: "strawberry juice, 15%"',
    composition: 'Composition',
    tnved: 'TN VED code',
    tnvedHint: 'Customs classification code',
    countryOfOrigin: 'Country of origin',
    capacity: 'Monthly capacity',
    capacityValue: 'How much',
    capacityUnit: 'Unit',
    packagingTitle: 'Packaging chain',
    packagingHint:
      'Describe how the product is packed: pieces per pack, packs per box, boxes per pallet, pallets per truck.',
    unitWeight: 'Weight of one piece, kg',
    unitWeightHint: 'Gross, per piece',
    piecesPerPack: 'Pieces per pack',
    packsPerBox: 'Packs per box',
    boxesPerPallet: 'Boxes per pallet',
    palletsPerTruck: 'Pallets per truck',
    palletsPerTruckHint:
      'How many pallets OF THIS PRODUCT fit a standard truck — it depends on how it stacks.',
    chainSummary: '1 truck = {{pallets}} pallets = {{boxes}} boxes = {{packs}} packs = {{pieces}} pieces',
    chainIncomplete: 'Fill in the whole chain to see the summary',
    priceTitle: 'Your price',
    priceValue: 'Price, USD',
    priceUnit: 'Per unit',
    pricePreview: '≈ {{price}} per piece',
    pricePreviewHint: 'This is how your price converts to a single piece.',
    priceIncomplete: 'Enter a price and complete the packaging chain to see the conversion',
    priceNotice: 'MOST sets the final catalog price; buyers never see your price.',
    mediaTitle: 'Photos and video',
    photos: 'Photos',
    photosHint:
      'Up to {{max}} photos: JPEG, PNG or WEBP, up to {{size}}. The first photo is the main one. Drag to reorder.',
    photoSlot: 'Slot {{n}}',
    slotFront: 'front',
    slotBack: 'back',
    slotLeft: 'left',
    slotRight: 'right',
    slotTop: 'top',
    slotBottom: 'bottom',
    slotExtra: 'additional',
    slotHintAdvisory: 'The labels are a hint, not a requirement.',
    video: 'Video',
    videoHint: 'One MP4 video, up to {{size}}. Optional.',
    dropHere: 'Drop files here',
    orPick: 'or pick from your computer',
    uploadRetry: 'Retry upload',
    uploadFailed: 'Upload failed',
    tooLarge: 'File is larger than {{size}}',
    badType: 'Unsupported file type',
    needPhoto: 'At least one photo is required',
    photoDeleteConfirm: 'Delete this photo?',
    photoDeleteLast: 'An active product cannot lose its last photo.',
    reviewTitle: 'Review',
    reviewHint: 'Check the details before sending to MOST.',
    activate: 'Activate',
    activateBlocked: 'To activate, fill in all required fields and add a photo.',
    activateSuccess: 'Sent to MOST for pricing',
    activateSuccessBody: 'The product appears in the buyer catalog once MOST sets the catalog price.',
    saveDraft: 'Save draft',
    saveOnly: 'Save',
    repriceWarnTitle: 'The product will go back to MOST for re-pricing',
    repriceWarnBody:
      'You are changing the price or packaging of a product that is already published. After saving, MOST will set the catalog price again.',
    repriceWarnHidden:
      'If the new price turns out to be at or above the current catalog price, the product will be hidden from buyers until MOST sets a new one.',
    repriceWarnConfirm: 'Understood, save',
  },

  orders: {
    title: 'Orders',
    subtitle: 'Only your own items from each order are shown here.',
    number: 'Number',
    date: 'Date',
    status: 'Status',
    buyerCountry: 'Buyer country',
    sliceTotal: 'Total for your items',
    emptyTitle: 'No orders yet',
    emptyBody: 'They appear here once a buyer orders your products.',
    detailTitle: 'Order {{number}}',
    yourItems: 'Your items',
    product: 'Product',
    qty: 'Quantity',
    pieces: 'Pieces',
    pricePerPiece: 'Your price per piece',
    lineTotal: 'Total',
    lineWeight: 'Weight',
    sliceWeight: 'Weight of your items',
    totalAtYourPrices: 'Total at your prices',
    instruction_contact_most: 'Contact MOST about order {{number}}.',
    instructionHint: 'MOST negotiates with the buyer and will share the details.',
    anonymityNote:
      'MOST never reveals the buyer to the producer, nor your price to the buyer. That is why only your items and your prices are shown here.',
    notFound: 'Order not found',
    notFoundBody: 'This order may not contain any of your products.',
  },

  notifications: {
    title: 'Notifications',
    markAllRead: 'Mark all as read',
    markRead: 'Mark read',
    unread: 'Unread',
    loadMore: 'Load more',
    emptyTitle: 'No notifications',
    emptyBody: 'Messages about orders and MOST decisions appear here.',

    expand: 'Details',
    collapse: 'Collapse',
    openOrder: 'Open order',

    // These keys MUST match the backend `Type` constants (notifications/domain.go).
    type: {
      order_created_producer: 'New order',
      order_created_producer_body: 'A buyer ordered your products. Order {{order_number}}.',
      order_status_changed: 'Order status changed',
      order_status_changed_body: 'Order {{order_number}} — new status: {{status}}.',
      product_priced: 'Product published',
      product_priced_body: 'MOST set the catalog price — "{{product_name}}" is visible to buyers.',
      producer_approved: 'Profile approved',
      producer_approved_body: 'MOST approved your profile. You can now add products.',
      producer_rejected: 'MOST asks you to update your profile',
      producer_rejected_body: 'Open your profile to read the comment and re-submit.',
      generic: 'Notification',
    },
  },

  errors: {
    'validation-failed': 'Please check the fields.',
    'not-found': 'Not found.',
    forbidden: 'Not allowed.',
    conflict: 'The data changed. Refresh and try again.',
    'rate-limited': 'Too many attempts. Please wait a moment.',
    'payload-too-large': 'The file is too large.',
    'unsupported-media-type': 'Unsupported file type.',
    field: {
      required: 'Required',
      invalid: 'Invalid value',
      too_short: 'Too short',
      too_long: 'Too long',
      duplicate: 'Already exists',
      min: 'Too small',
      max: 'Too large',
    },
  },

  validation: {
    required: 'Required',
    minLength: 'At least {{n}} characters',
    integerMin1: 'A whole number, at least 1',
    positive: 'Must be greater than zero',
    number: 'Enter a number',
    country2: 'Two-letter country code',
    price: 'Enter a price, e.g. 8.40',
  },
  chat: {
    title: 'MOST Support',
    subtitle: "We're here — products, pricing, orders",
    open: 'Open support chat',
    close: 'Close chat',
    placeholder: 'Type a message…',
    send: 'Send',
    attach: 'Attach a file',
    record: 'Record a voice message',
    stopRecording: 'Stop recording',
    loadOlder: 'Show earlier messages',
    empty: "Write to us — we'll help with products, pricing and orders.",
    sendError: 'Could not send. Please try again.',
    call: {
      audio: 'Audio call to support',
      video: 'Video call to support',
      incoming: 'Incoming call from support',
      outgoing: 'Calling support…',
      connecting: 'Connecting…',
      active: 'In call',
      endedStatus: 'Call ended',
      accept: 'Answer',
      decline: 'Decline',
      hangUp: 'Hang up',
      completed: 'Call',
      missed: 'Missed call',
      declined: 'Call declined',
      mediaDenied: 'No access to microphone or camera',
      connectionLost: 'Connection lost',
      failed: 'Could not start the call',
    },
  },
};
